import React, { Dispatch, forwardRef, SetStateAction, useImperativeHandle, useState } from "react";
import { Button } from "primereact/button";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import * as StorageConstants from "../../Models/SecureStorageConstants";
import SignInResponse from "../../Models/Authentication/SignInResponse";
import { signInOtpService, signInService } from "../../Services/authenticationService";
import ApiResponse from "../../Models/ApiResponse";
import { useTranslation } from "react-i18next";
import SignInOTPResponse from "../../Models/Authentication/SignInOTPResponse";
import Authentication from "./Authentication";
import { UserService } from "../../Services/userService";
import applyCustomerStyling from "../../Template/custom-styling/custom-styling";
import { reloadBrowserBrandingForSession } from "../../Template/custom-styling/applyBrowserBranding";
import { initializeI18n } from "../../Translations/i18n";
import { CustomerService } from "../../Services/customerService";
import { warmCustomerLogoCacheBounded } from "../../Template/custom-styling/customerLogoCache";
import { preloadRouteForPath } from "../../routes/lazyRoutes";
// Direct imports instead of the Components/inputs barrel: the sign-in page is part of the
// eager shell and the barrel would pull ~30 input modules into the startup bundle.
import { DashlyPasswordInput } from "../../Components/inputs/DashlyPasswordInput/DashlyPasswordInput";
import { DashlyTextInput } from "../../Components/inputs/DashlyTextInput/DashlyTextInput";

interface LoginFormProps<F, C> extends Omit<any, any> {
    username?: string;
    password?: string;
    toggleForgotPassword: () => void;
    toggleResetPassword: () => void;
    setSuccessMessage: Dispatch<SetStateAction<string | null>>,
    setErrorMessage: Dispatch<SetStateAction<string | null>>,
}

export interface LoginFormRef {
    signInAction: (username: string, password: string) => void;
}

const SignInForm = forwardRef<LoginFormRef, LoginFormProps<any, any>>(<F, C>({
    setSuccessMessage,
    setErrorMessage,
    toggleForgotPassword,
    toggleResetPassword,
}: LoginFormProps<F, C>, ref: React.Ref<LoginFormRef> | undefined) => {
    const { handleSubmit, control } = useForm<any>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [signInOTPResponse, setSignInOTPResponse] = useState<SignInOTPResponse>();
    const [data, setData] = useState();
    const [loading, setLoading] = useState(false);

    useImperativeHandle(ref, () => ({
        signInAction: signInAction
    }));

    const signInAction = (username: any, password: any) => {
        signInService({
            "username": username,
            "password": password
        }).then((response) => {

            if (response.status === 200) {
                handleJwtResponse(response.data);
            } else if (response.status === 202 && response.data.internalCode === "IMSECSGOTP") {
                setErrorMessage(null);
                let apiResponse = response.data as ApiResponse<SignInOTPResponse>;
                setSignInOTPResponse(apiResponse.data);
            } else if (response.status === 202 && response.data.internalCode === "IMSECUSEXP") {
                if (response.data.data) {
                    setErrorMessage(t('authentication.account-expired'))
                    setSuccessMessage(null);
                    toggleResetPassword();
                    const path = response.data.data.replace('reset-password/', 'reset-password/E');
                    navigate('/' + path, { replace: true });
                } else {
                    setSuccessMessage(null);
                }
            } else {
                setErrorMessage(response.data.internalCode);
            }
        }).catch((axiosError) => {
            setErrorMessage(axiosError.response.data.message);
            setLoading(false)
        })
    }

    const onSubmit: SubmitHandler<any> = ((data) => {
        setLoading(true)
        setData(data)
        signInAction(data.username, data.password);
    });

    const fetchStyling = async () => {
        try {
            const result = await new UserService().getCustomerStyling();
            if (result.data && Array.isArray(result.data) && result.data.length > 0) {
                secureLocalStorage.setItem(StorageConstants.CUSTOMER_STYLING, JSON.stringify(result.data[0]));
            }
            applyCustomerStyling();

        } catch (error) {
            console.error("Error fetching customer styling", error);
        }
    };
    const handleJwtResponse = async (apiResponse: ApiResponse<SignInResponse>) => {
        const { accessToken, refreshToken, user, permissions } = apiResponse.data;
        secureLocalStorage.setItem(StorageConstants.ACCESS_TOKEN, accessToken);
        secureLocalStorage.setItem(StorageConstants.REFRESH_TOKEN, refreshToken);
        secureLocalStorage.setItem(StorageConstants.USER, JSON.stringify(user));
        secureLocalStorage.setItem(StorageConstants.PERMISSIONS, JSON.stringify(permissions));

        await initializeI18n();

        await fetchStyling();
        await reloadBrowserBrandingForSession();

        // Warm the customer-logo cache *before* entering the app, so the sidebar paints the
        // customer logo on its first frame instead of the Dashly one. Bounded: a slow or
        // failing logo request never holds the sign-in up for more than the timeout.
        // (The previous fire-and-forget version also checked for an array response while
        // the endpoint returns an object, so it never actually filled the cache.)
        // SecurityUserResponse carries the customer as `customerUuid`; fall back to /me when absent.
        let customerUuid: string | null = user?.customerUuid || null;
        if (!customerUuid) {
            try {
                const meResponse = await new UserService().retrieveMe();
                customerUuid = meResponse?.data?.data?.customer?.uuid ?? null;
            } catch {
                customerUuid = null;
            }
        }
        await warmCustomerLogoCacheBounded(customerUuid);

        // Warm the landing page's chunk now, in parallel with the remaining steps, so the
        // navigation below does not wait on a network round trip. Not awaited on purpose.
        void preloadRouteForPath("/");

        if (customerUuid) {
            // Wide logo is not needed for the first paint; keep it in the background.
            const storedLogosWide = JSON.parse(localStorage.getItem('customerLogosWide') || '{}');
            if (!storedLogosWide[customerUuid]) {
                const wideCustomerUuid = customerUuid;
                new CustomerService().getCustomerLogoWide(wideCustomerUuid)
                    .then(logoWideResponse => {
                        if (logoWideResponse?.data?.data && logoWideResponse.data.data.logo) {
                            storedLogosWide[wideCustomerUuid] = logoWideResponse.data.data.logo;
                            localStorage.setItem('customerLogosWide', JSON.stringify(storedLogosWide));
                        }
                    })
                    .catch(error => {
                        console.error("Error fetching wide logo:", error);
                    });
            }
        }

        navigate('/', { replace: true });
    }

    const onSubmitOtp: SubmitHandler<any> = ((data) => {
        signInOtpService({
            otp: data.otp,
            token: signInOTPResponse?.token ?? '',
        }).then((response) => {
            handleJwtResponse(response.data);
        }).catch((axiosError) => {
            switch (axiosError.response.data.internalCode) {
                case "APIBDRQOTP":
                    setErrorMessage(t('mfa.invalid.login'));
                    break;
                default:
                    setErrorMessage(t('common.error'));
            }
        });
    })


    return signInOTPResponse !== undefined && signInOTPResponse !== null ? (
        <Authentication setErrorMessage={setErrorMessage} setSuccessMessage={setSuccessMessage}
            signInOTPResponse={signInOTPResponse} onSubmit={onSubmitOtp} mfaSend={onSubmit} data={data} />
    ) : (
        <form className="sign-in__form" onSubmit={handleSubmit(onSubmit)}>
            <div className="sign-in__fields">
                <Controller
                    name="username"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                        <DashlyTextInput
                            {...field}
                            value={field.value ?? ""}
                            placeholder={t("login.username")}
                            autoComplete="username"
                            autoFocus
                            disabled={loading}
                            required
                        />
                    )}
                />

                <Controller
                    name="password"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                        <DashlyPasswordInput
                            {...field}
                            value={field.value ?? ""}
                            placeholder={t("login.password")}
                            toggleMask
                            feedback={false}
                            disabled={loading}
                            required
                        />
                    )}
                />
            </div>

            <div className="sign-in__footer">
                <a
                    onClick={toggleForgotPassword}
                    className="sign-in__forgot-link"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            toggleForgotPassword();
                        }
                    }}
                >
                    {t("login.forgot-password")}
                </a>
                <Button className="sign-in__submit" type="submit" loading={loading} label={t("login.sign-in")} />
            </div>
        </form>
    )
});

export { SignInForm };
export type { LoginFormProps };
